import { isUSTimezone } from "./usTimezones";

export type TimezoneMode = "et" | "local" | "custom";
export type TimeFormat = "12h" | "24h";

const TZ_STORAGE_KEY = "lh:timezone";
const TIME_FORMAT_KEY = "lh:timeFormat";

function detectBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";
  } catch {
    return "America/New_York";
  }
}

export function resolveTimezone(
  mode: TimezoneMode | null,
  customTz?: string | null,
): string {
  if (mode === "et") return "America/New_York";
  if (mode === "custom") {
    if (customTz && isUSTimezone(customTz)) return customTz;
    return "America/New_York";
  }
  return detectBrowserTimezone();
}

export function getTimezone(): string {
  try {
    const stored = sessionStorage.getItem(TZ_STORAGE_KEY);
    if (stored) return stored;
  } catch {
    // sessionStorage may be unavailable
  }
  return detectBrowserTimezone();
}

export function setTimezone(tz: string): void {
  try {
    sessionStorage.setItem(TZ_STORAGE_KEY, tz);
  } catch {
    // ignore
  }
}

export function clearTimezone(): void {
  try {
    sessionStorage.removeItem(TZ_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function getTimeFormat(): TimeFormat {
  try {
    const stored = sessionStorage.getItem(TIME_FORMAT_KEY);
    if (stored === "24h" || stored === "12h") return stored;
  } catch {
    // sessionStorage may be unavailable
  }
  return "24h";
}

export function setTimeFormat(fmt: TimeFormat): void {
  try {
    sessionStorage.setItem(TIME_FORMAT_KEY, fmt);
  } catch {
    // ignore
  }
}

export function clearTimeFormat(): void {
  try {
    sessionStorage.removeItem(TIME_FORMAT_KEY);
  } catch {
    // ignore
  }
}

// ── Timezone mode (preserves user intent from account page) ─────
// getTimezone() returns the resolved IANA string, but we also need
// the original mode ("et" | "local" | "custom") to show the correct
// label in column headers (e.g. "DATETIME (ET)").

const TZ_MODE_KEY = "lh:timezoneMode";
const TZ_CUSTOM_KEY = "lh:timezoneCustom";

export function setTimezoneMode(mode: TimezoneMode, customTz?: string | null): void {
  try {
    sessionStorage.setItem(TZ_MODE_KEY, mode);
    if (customTz) sessionStorage.setItem(TZ_CUSTOM_KEY, customTz);
    else sessionStorage.removeItem(TZ_CUSTOM_KEY);
  } catch {
    // ignore
  }
}

export function getTimezoneMode(): TimezoneMode | null {
  try {
    const stored = sessionStorage.getItem(TZ_MODE_KEY);
    if (stored === "et" || stored === "local" || stored === "custom") return stored;
  } catch {
    // ignore
  }
  return null;
}

function timezoneToShortLabel(tz: string): string {
  // Map common IANA zones to short abbreviations
  const map: Record<string, string> = {
    "America/New_York": "ET",
    "America/Chicago": "CT",
    "America/Denver": "MT",
    "America/Los_Angeles": "PT",
    "America/Anchorage": "AKT",
    "America/Phoenix": "MST",
    "Europe/London": "GMT",
    "Europe/Paris": "CET",
    "Europe/Berlin": "CET",
    "Asia/Kolkata": "IST",
    "Asia/Tokyo": "JST",
    "Asia/Shanghai": "CST",
    "Asia/Dubai": "GST",
    "Australia/Sydney": "AEST",
    "Pacific/Auckland": "NZST",
  };
  if (map[tz]) return map[tz];
  // Fallback: last part of IANA zone, e.g. "America/Argentina/Buenos_Aires" → "Buenos Aires"
  const parts = tz.split("/");
  return parts[parts.length - 1].replace(/_/g, " ").toUpperCase();
}

/**
 * Returns a short label for the user's selected timezone mode, matching
 * the account page setting. Examples: "ET", "Local", "PST".
 */
export function getTimezoneLabel(): string {
  const mode = getTimezoneMode();
  if (mode === "et") return "ET";
  if (mode === "local") return "Local";
  if (mode === "custom") {
    try {
      const customTz = sessionStorage.getItem(TZ_CUSTOM_KEY);
      if (customTz) return timezoneToShortLabel(customTz);
    } catch {
      // ignore
    }
  }
  // Fallback: derive from resolved timezone
  return timezoneToShortLabel(getTimezone());
}

function parseUtc(value: string): Date {
  if (!value) return new Date(NaN);
  return new Date(value.endsWith("Z") ? value : `${value}Z`);
}

export function formatTime(iso: string): string {
  if (!iso) return "";
  const tz = getTimezone();
  const hour12 = getTimeFormat() === "12h";
  return parseUtc(iso).toLocaleTimeString("en-US", {
    timeZone: tz,
    hour: "2-digit",
    minute: "2-digit",
    hour12,
  });
}

export function formatDate(iso: string): string {
  if (!iso) return "";
  const tz = getTimezone();
  return parseUtc(iso).toLocaleDateString("en-US", {
    timeZone: tz,
    month: "long",
    day: "numeric",
  });
}

export function formatDateTime(iso: string): string {
  if (!iso) return "";
  return `${formatDate(iso)}, ${formatTime(iso)}`;
}

export function todayInTz(): string {
  const tz = getTimezone();
  return new Date().toLocaleDateString("en-CA", { timeZone: tz });
}

export function hourInTz(now?: Date): number {
  const tz = getTimezone();
  return Number(
    new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hour: "numeric",
      hourCycle: "h23",
    }).format(now ?? new Date()),
  );
}

export function isPastDate(effectiveDate: string, today: string): boolean {
  return effectiveDate !== today;
}

export function formatUpdatedTimestamp(
  iso: string,
  effectiveDate: string,
  today: string,
): string {
  if (!iso) return "";
  const time = formatTime(iso);
  if (isPastDate(effectiveDate, today)) {
    // `effectiveDate` is an exchange-session calendar date, not an instant.
    // Formatting the observation timestamp here can shift the displayed
    // session forward or backward for users outside the exchange timezone.
    const effectiveInstant = new Date(`${effectiveDate}T12:00:00Z`);
    const datePart = Number.isNaN(effectiveInstant.getTime())
      ? formatDate(iso)
      : effectiveInstant.toLocaleDateString("en-US", {
          timeZone: "UTC",
          month: "long",
          day: "numeric",
        });
    return `Updated ${datePart}, ${time}`;
  }
  return `Updated ${time}`;
}

export function formatLastUpdateDate(date: Date | null): string {
  if (!date) return "";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
}
